import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LandingPage.css';
import MvpModal from './MvpModal';

/* ============================================
   Animated Counter Hook
============================================ */
function useAnimatedCounter(target, duration = 1500, trigger = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, trigger]);
  return count;
}

/* ============================================
   Intersection Observer Hook
============================================ */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ============================================
   Reveal wrapper component
============================================ */
function Reveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? 'visible' : ''} ${delay ? `reveal-delay-${delay}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

/* ============================================
   SVG Icons
============================================ */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" stroke="var(--accent)">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" stroke="#FF4D4D">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="faq-chevron">
    <path d="M6 9l6 6 6-6" />
  </svg>
);


/* ============================================
   Tour Steps Configuration
============================================ */
const TOUR_STEPS = [
  { target: '.hero', message: 'Bienvenido a tuMVP.uy — Acá arranca todo. Tu idea convertida en producto real.', clickTarget: '.badge' },
  { target: '.price-section', message: 'Precio fijo: USD 950. Sin sorpresas. Un pago y listo.', clickTarget: '.price-card' },
  { target: '#como-funciona', message: '3 pasos simples: contanos tu idea, la construimos, y validás con usuarios reales.', clickTarget: '.step' },
  { target: '#ejemplos', message: 'Mirá ejemplos concretos de MVPs: marketplace, SaaS, dashboards. Así se ve lo que entregamos.', clickTarget: '.case' },
  { target: '#probalos', message: 'No te lo contamos — probalo vos mismo. MVPs reales funcionando en vivo.', clickTarget: '.btn-live' },
  { target: '#faq', message: 'Preguntas frecuentes: todo lo que necesitás saber antes de arrancar.', clickTarget: '.faq-item' },
  { target: '#contacto', message: '¿Listo? Empezá tu MVP hoy. Click en el botón y arrancamos.', clickTarget: '.cta-main' },
];

/* ============================================
   GuidedTour Component
============================================ */
function GuidedTour({ active, onClose }) {
  const [step, setStep] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [clicking, setClicking] = useState(false);
  const [ripple, setRipple] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (progressRef.current) { cancelAnimationFrame(progressRef.current); progressRef.current = null; }
  }, []);

  const startAutoAdvance = useCallback(() => {
    clearTimers();
    setProgress(0);
    const start = performance.now();
    const duration = 5000;
    const tick = (now) => {
      const elapsed = now - start;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);
      if (p < 1) {
        progressRef.current = requestAnimationFrame(tick);
      }
    };
    progressRef.current = requestAnimationFrame(tick);
    timerRef.current = setTimeout(() => {
      setStep(prev => {
        if (prev < TOUR_STEPS.length - 1) return prev + 1;
        onClose();
        return 0;
      });
    }, duration);
  }, [clearTimers, onClose]);

  const goToStep = useCallback((targetEl, clickTargetSel) => {
    setTransitioning(true);
    targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      const rect = targetEl.getBoundingClientRect();
      const pad = 12;
      setSpotlightRect({
        top: rect.top - pad + window.scrollY,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      });

      const clickEl = clickTargetSel ? targetEl.querySelector(clickTargetSel) || targetEl : targetEl;
      const clickRect = clickEl.getBoundingClientRect();
      const cx = clickRect.left + clickRect.width * 0.4;
      const cy = clickRect.top + clickRect.height * 0.4;
      setCursorPos({ x: cx, y: cy });

      setTimeout(() => {
        setClicking(true);
        setRipple({ x: cx, y: cy });
        setTimeout(() => {
          setClicking(false);
          setRipple(null);
          setTransitioning(false);
          startAutoAdvance();
        }, 600);
      }, 900);
    }, 600);
  }, [startAutoAdvance]);

  useEffect(() => {
    if (!active) { clearTimers(); return; }
    const current = TOUR_STEPS[step];
    const el = document.querySelector(current.target);
    if (el) goToStep(el, current.clickTarget);
    return clearTimers;
  }, [active, step, goToStep, clearTimers]);

  useEffect(() => {
    if (!active) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, onClose]);

  if (!active) return null;

  return (
    <>
      {/* Overlay */}
      <div className="tour-overlay" onClick={onClose} />

      {/* Spotlight */}
      {spotlightRect && (
        <div
          className="tour-spotlight"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      )}

      {/* Cursor */}
      <svg
        className={`tour-cursor ${clicking ? 'clicking' : ''}`}
        style={{ left: cursorPos.x, top: cursorPos.y }}
        width="24" height="24" viewBox="0 0 24 24" fill="none"
      >
        <path d="M5 3l14 8-6.5 2L9 19.5 5 3z" fill="white" stroke="var(--accent)" strokeWidth="1.5" />
      </svg>

      {/* Ripple */}
      {ripple && (
        <div className="tour-ripple" style={{ left: ripple.x, top: ripple.y }} />
      )}

      {/* Tooltip */}
      <div className={`tour-tooltip ${transitioning ? 'transitioning' : ''}`}>
        <div className="tour-tooltip-step">Paso {step + 1} de {TOUR_STEPS.length}</div>
        <p className="tour-tooltip-msg">{TOUR_STEPS[step].message}</p>
        <div className="tour-tooltip-progress">
          <div className="tour-tooltip-progress-bar" style={{ width: `${progress * 100}%` }} />
        </div>
        <div className="tour-tooltip-actions">
          <button
            className="tour-btn"
            onClick={() => { clearTimers(); setStep(s => Math.max(0, s - 1)); }}
            disabled={step === 0}
          >
            ← Anterior
          </button>
          <button className="tour-btn tour-btn-close" onClick={onClose}>Salir</button>
          <button
            className="tour-btn tour-btn-next"
            onClick={() => {
              clearTimers();
              if (step < TOUR_STEPS.length - 1) setStep(s => s + 1);
              else onClose();
            }}
          >
            {step < TOUR_STEPS.length - 1 ? 'Siguiente →' : 'Finalizar ✓'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ============================================
   ThemeSelector Component
============================================ */
function ThemeSelector({ theme, setTheme }) {
  return (
    <div className="theme-selector">
      <button
        className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
        onClick={() => setTheme('dark')}
        title="Tema Dark"
      >
        <span className="theme-swatch theme-swatch-dark" />
        Dark
      </button>
      <button
        className={`theme-btn ${theme === 'clasico' ? 'active' : ''}`}
        onClick={() => setTheme('clasico')}
        title="Tema Clásico"
      >
        <span className="theme-swatch theme-swatch-clasico" />
        Clásico
      </button>
      <button
        className={`theme-btn ${theme === 'mercadolibre' ? 'active' : ''}`}
        onClick={() => setTheme('mercadolibre')}
        title="Tema Mercado Libre"
      >
        <span className="theme-swatch theme-swatch-ml" />
        ML
      </button>
    </div>
  );
}

/* ============================================
   FAQ Item Component
============================================ */
function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <div className="faq-q" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <ChevronDown />
      </div>
      <div className="faq-a-wrapper">
        <div className="faq-a">{answer}</div>
      </div>
    </div>
  );
}

/* ============================================
   MAIN LANDING PAGE
============================================ */
function LandingPage({ autoOpenModal = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [modalOpen, setModalOpen] = useState(autoOpenModal);
  const [priceRef, priceInView] = useInView(0.3);
  const priceCount = useAnimatedCounter(950, 1200, priceInView);
  const [tourActive, setTourActive] = useState(true);
  const [theme, setTheme] = useState('dark');

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const startTour = useCallback(() => setTourActive(true), []);
  const closeTour = useCallback(() => setTourActive(false), []);

  // Sticky nav scroll detection
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="landing-page" data-theme={theme}>
      {/* Background Orbs */}
      <div className="bg-orbs">
        <div className="bg-orb" />
        <div className="bg-orb" />
        <div className="bg-orb" />
      </div>

      {/* ========== NAV ========== */}
      <header className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <span className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            tu<span>MVP</span>.uy
          </span>

          <div className="nav-links">
            <a href="#como-funciona" onClick={(e) => { e.preventDefault(); scrollTo('como-funciona'); }}>Cómo funciona</a>
            <a href="#ejemplos" onClick={(e) => { e.preventDefault(); scrollTo('ejemplos'); }}>Ejemplos</a>
            <a href="#probalos" onClick={(e) => { e.preventDefault(); scrollTo('probalos'); }}>Probalos</a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo('faq'); }}>FAQ</a>
            <button className="nav-cta" onClick={openModal}>Empezá tu MVP →</button>
          </div>
        </div>
      </header>

      <div className="container">

        {/* ========== HERO ========== */}
        <section className="hero">
          <div className="badge">
            <div className="badge-dot" />
            Prototipado rápido en Uruguay
          </div>
          <h1>
            Tu idea,<br />
            <span className="highlight">funcionando en días.</span>
            <span className="typed-cursor" />
          </h1>
          <p className="hero-sub">
            Convertimos tu idea en un producto funcional por precio fijo.
            Validá tu mercado en semanas, no en meses. El código es tuyo desde el día uno.
          </p>

          <button className="cta-tour" onClick={startTour}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            Tour guiado
          </button>

          {/* Stats bar */}
          <div className="stats-bar">
            <div className="stat-item">
              <div className="stat-value">USD 950</div>
              <div className="stat-label">Precio fijo</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">1–3 sem</div>
              <div className="stat-label">Entrega</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">100%</div>
              <div className="stat-label">Tu código</div>
            </div>
          </div>
        </section>

        {/* ========== PRICE ========== */}
        <div className="price-section" ref={priceRef}>
          <div className="price-card">
            <div className="price-label">Precio fijo — Primer MVP</div>
            <div className="price-amount">
              <span className="currency">USD</span> {priceCount}
            </div>
            <p className="price-note">Pago único. Sin costos ocultos.</p>
            <div className="price-divider" />
            <div className="price-features">
              <div className="price-feature">
                <div className="check"><CheckIcon /></div>
                <span>Aplicación web funcional con las features core de tu idea</span>
              </div>
              <div className="price-feature">
                <div className="check"><CheckIcon /></div>
                <span>Código fuente completo — es tuyo, siempre</span>
              </div>
              <div className="price-feature">
                <div className="check"><CheckIcon /></div>
                <span>Deploy incluido en tumvp.uy o descargá los fuentes</span>
              </div>
              <div className="price-feature">
                <div className="check"><CheckIcon /></div>
                <span>Diseño responsive, listo para mostrar a inversores y usuarios</span>
              </div>
            </div>
            <button className="cta-main cta-full" onClick={openModal}>
              Quiero mi MVP →
            </button>
          </div>
        </div>

        {/* ========== HOW IT WORKS ========== */}
        <section className="steps-section" id="como-funciona">
          <Reveal>
            <div className="section-label">Cómo funciona</div>
            <div className="section-title">De la idea al producto<br />en 3 pasos</div>
          </Reveal>
          <div className="steps">
            {[
              { num: '01', title: 'Contanos tu idea', desc: 'Nos juntamos (virtual o presencial) para entender el problema que resolvés y definir juntos las funcionalidades esenciales del MVP.' },
              { num: '02', title: 'Construimos rápido', desc: 'Desarrollamos tu MVP con foco en velocidad y calidad. Iteramos con vos en el proceso para asegurarnos que estemos alineados.' },
              { num: '03', title: 'Validá con usuarios reales', desc: 'Tu producto queda online y listo para recibir usuarios. Empezá a validar tu hipótesis de mercado desde el día uno.' },
            ].map((step, i) => (
              <Reveal key={step.num} delay={i + 1}>
                <div className="step">
                  <div className="step-number">{step.num}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ========== DELIVERY OPTIONS ========== */}
        <section className="delivery">
          <Reveal>
            <div className="section-label">Entrega</div>
            <div className="section-title">Vos elegís cómo<br />recibir tu MVP</div>
          </Reveal>
          <div className="delivery-grid">
            <Reveal delay={1}>
              <div className="delivery-card featured">
                <div className="delivery-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
                </div>
                <h3>Hosting en tuMVP.uy</h3>
                <p>Nos encargamos de todo: instalación, dominio, SSL y puesta en marcha. Tu MVP queda online y funcionando sin que tengas que tocar nada técnico. Ideal para validar rápido.</p>
                <div className="delivery-tag">Incluido en el precio</div>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="delivery-card">
                <div className="delivery-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
                <h3>Descargá los fuentes</h3>
                <p>Te entregamos el repositorio completo con documentación para que lo instales en tu propia infraestructura. Tu código, tu servidor, tu control total.</p>
                <div className="delivery-tag">Incluido en el precio</div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ========== WHAT YOU GET ========== */}
        <section className="what-you-get">
          <Reveal>
            <div className="section-label">Qué incluye</div>
            <div className="section-title">Todo lo que necesitás<br />para salir al mercado</div>
          </Reveal>
          <div className="features-grid">
            {[
              { icon: '⚡', title: 'App web funcional', desc: 'No es un mockup — es software real que tus usuarios pueden usar.' },
              { icon: '📱', title: 'Diseño responsive', desc: 'Funciona en desktop, tablet y mobile. Listo para mostrar.' },
              { icon: '🔐', title: 'Autenticación de usuarios', desc: 'Login, registro y gestión básica de cuentas si tu MVP lo requiere.' },
              { icon: '💾', title: 'Base de datos', desc: 'Persistencia de datos configurada y lista para producción.' },
              { icon: '📦', title: 'Código fuente completo', desc: 'Repositorio Git con README y documentación para continuar el desarrollo.' },
              { icon: '🚀', title: 'Deploy listo', desc: 'Configuración de deploy incluida, ya sea en nuestro hosting o el tuyo.' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i % 2 === 0 ? 1 : 2}>
                <div className="feature-item">
                  <div className="icon">{f.icon}</div>
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ========== MVP EXAMPLES ========== */}
        <section className="examples-section" id="ejemplos">
          <Reveal>
            <div className="section-label">¿Qué es un MVP?</div>
            <div className="section-title">Un MVP se ve así</div>
            <p className="section-desc">¿Qué entra en un MVP? Estos tres ejemplos ilustrativos muestran el tipo de producto, alcance y funcionalidades que se pueden construir. Cada uno representa una vertical distinta para que puedas dimensionar tu propia idea.</p>
          </Reveal>

          <Reveal>
            <div className="examples-disclaimer">
              <div className="disclaimer-icon">💡</div>
              <div>Los siguientes casos son <strong>ejemplos ilustrativos</strong> — se presentan para que visualices qué tipo de producto se puede lograr con un MVP, qué features incluiría y en qué plazo estimado. No representan proyectos ejecutados ni clientes reales.</div>
            </div>
          </Reveal>

          {/* EXAMPLE 1: FletePro */}
          <Reveal>
            <article className="case">
              <div className="case-header">
                <div>
                  <div className="case-badge">Ejemplo: Marketplace</div>
                  <h3>FletePro</h3>
                  <p className="case-tagline">Un marketplace de fletes y mudanzas que conecta a quienes necesitan enviar algo con fleteros independientes. Cotización al instante, aceptación en un toque, seguimiento en vivo.</p>
                </div>
                <div className="case-meta">
                  <div className="meta-chip">
                    <span className="val">~2 sem</span>
                    <span className="label-sm">Estimado</span>
                  </div>
                  <div className="meta-chip">
                    <span className="val">$950</span>
                    <span className="label-sm">Precio fijo</span>
                  </div>
                </div>
              </div>
              <div className="case-body">
                <div className="case-grid">
                  <div className="case-section">
                    <div className="case-section-label">El problema</div>
                    <p>Contratar un flete es caótico: grupos de Facebook, llamadas sin respuesta, precios arbitrarios y cero visibilidad del estado del envío. Los fleteros independientes pierden tiempo negociando en lugar de trabajando.</p>
                  </div>
                  <div className="case-section">
                    <div className="case-section-label">La solución MVP</div>
                    <p>Una web app donde el usuario ingresa origen, destino y tipo de carga. El sistema calcula una cotización estimada y la publica a fleteros cercanos. El primero en aceptar toma el trabajo. Ambas partes ven el estado en tiempo real.</p>
                  </div>
                </div>
                <div className="case-section">
                  <div className="case-section-label">Features posibles del MVP</div>
                  <div className="case-features">
                    {['Cotizador automático', 'Registro fleteros', 'Mapa en vivo', 'Sistema de aceptación', 'Historial de envíos', 'Notificaciones email', 'Panel de admin'].map(f => (
                      <span className="pill" key={f}>{f}</span>
                    ))}
                  </div>
                </div>
                <div className="case-stack">
                  <span className="stack-label-sm">Stack posible:</span>
                  {['Next.js', 'PostgreSQL', 'Leaflet Maps', 'Resend', 'Vercel'].map(t => (
                    <span className="stack-tag" key={t}>{t}</span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>

          {/* EXAMPLE 2: MiTurno */}
          <Reveal>
            <article className="case">
              <div className="case-header">
                <div>
                  <div className="case-badge">Ejemplo: SaaS</div>
                  <h3>MiTurno</h3>
                  <p className="case-tagline">Sistema de reserva de turnos online para clínicas y consultorios. Los pacientes agendan solos, la recepción deja de vivir pegada al teléfono.</p>
                </div>
                <div className="case-meta">
                  <div className="meta-chip">
                    <span className="val">~10 días</span>
                    <span className="label-sm">Estimado</span>
                  </div>
                  <div className="meta-chip">
                    <span className="val">$950</span>
                    <span className="label-sm">Precio fijo</span>
                  </div>
                </div>
              </div>
              <div className="case-body">
                <div className="case-grid">
                  <div className="case-section">
                    <div className="case-section-label">El problema</div>
                    <p>Las clínicas pequeñas y consultorios todavía gestionan turnos por teléfono o WhatsApp. Esto genera pérdida de tiempo, turnos duplicados, pacientes que no aparecen y una recepcionista colapsada.</p>
                  </div>
                  <div className="case-section">
                    <div className="case-section-label">La solución MVP</div>
                    <p>Una plataforma multi-tenant donde cada clínica tiene su propia agenda online. Los pacientes eligen profesional, día y hora desde una interfaz pública. La clínica gestiona todo desde un panel con vista calendario.</p>
                  </div>
                </div>
                <div className="case-section">
                  <div className="case-section-label">Features posibles del MVP</div>
                  <div className="case-features">
                    {['Agenda por profesional', 'Reserva pública online', 'Panel admin clínica', 'Recordatorios automáticos', 'Vista calendario semanal', 'Multi-tenant', 'Bloqueo de horarios'].map(f => (
                      <span className="pill" key={f}>{f}</span>
                    ))}
                  </div>
                </div>
                <div className="case-stack">
                  <span className="stack-label-sm">Stack posible:</span>
                  {['React', 'Node.js', 'PostgreSQL', 'Resend', 'Railway'].map(t => (
                    <span className="stack-tag" key={t}>{t}</span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>

          {/* EXAMPLE 3: CampoData */}
          <Reveal>
            <article className="case">
              <div className="case-header">
                <div>
                  <div className="case-badge">Ejemplo: Dashboard / AgTech</div>
                  <h3>CampoData</h3>
                  <p className="case-tagline">Dashboard de monitoreo agrícola que centraliza sensores, clima y rendimiento en una sola pantalla. Datos dispersos, una sola vista.</p>
                </div>
                <div className="case-meta">
                  <div className="meta-chip">
                    <span className="val">~2 sem</span>
                    <span className="label-sm">Estimado</span>
                  </div>
                  <div className="meta-chip">
                    <span className="val">$950</span>
                    <span className="label-sm">Precio fijo</span>
                  </div>
                </div>
              </div>
              <div className="case-body">
                <div className="case-grid">
                  <div className="case-section">
                    <div className="case-section-label">El problema</div>
                    <p>Los productores rurales manejan datos de múltiples fuentes: sensores de humedad, estaciones meteorológicas, planillas de rendimiento. Toda esta información vive en silos separados y tomar decisiones requiere cruzar datos manualmente.</p>
                  </div>
                  <div className="case-section">
                    <div className="case-section-label">La solución MVP</div>
                    <p>Un dashboard web que integra datos de sensores IoT vía API, información climática y permite al productor cargar datos de rendimiento por parcela. Todo se visualiza en gráficos interactivos con alertas configurables.</p>
                  </div>
                </div>
                <div className="case-section">
                  <div className="case-section-label">Features posibles del MVP</div>
                  <div className="case-features">
                    {['Ingesta datos IoT (API)', 'Widget clima en vivo', 'Gráficos de rendimiento', 'Alertas por umbral', 'Gestión de parcelas', 'Exportar a CSV', 'Vista mobile'].map(f => (
                      <span className="pill" key={f}>{f}</span>
                    ))}
                  </div>
                </div>
                <div className="case-stack">
                  <span className="stack-label-sm">Stack posible:</span>
                  {['Next.js', 'Chart.js', 'Supabase', 'OpenWeather API', 'Vercel'].map(t => (
                    <span className="stack-tag" key={t}>{t}</span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>
        </section>

        {/* ========== LIVE MVP CATALOG ========== */}
        <section className="live-mvps" id="probalos">
          <Reveal>
            <div className="section-label">MVPs reales — Probalos ahora</div>
            <div className="section-title">No te lo contamos.<br /><span style={{ color: 'var(--accent)' }}>Probalo vos mismo.</span></div>
            <p className="section-intro">
              Construimos MVPs funcionales que podés explorar en vivo. Cada uno está online, disponible para que te registres como usuario demo y recorras todas sus funcionalidades. <strong>Así se ve un MVP real — esto es lo que entregamos.</strong>
            </p>
          </Reveal>

          <div className="catalog-grid">
            <Reveal>
              <div className="catalog-card">
                <div className="catalog-top">
                  <div className="catalog-info">
                    <div className="catalog-type">Marketplace</div>
                    <h3>Marketplace MVP</h3>
                    <p className="catalog-desc">Un marketplace completo con publicación de productos, búsqueda avanzada, gestión de stock, métodos de envío y pago. Todo lo que necesita un marketplace para funcionar — en su versión mínima y lista para validar.</p>

                    <div className="catalog-features-grid">
                      {[
                        { icon: '🔍', text: 'Búsqueda con filtros y resultados random' },
                        { icon: '🕘', text: 'Historial de búsquedas por usuario' },
                        { icon: '📦', text: 'Gestión de stock en tiempo real' },
                        { icon: '📄', text: 'Publicaciones únicas o repetitivas' },
                        { icon: '🚚', text: 'Configuración de métodos de envío' },
                        { icon: '💳', text: 'Integración de métodos de pago' },
                        { icon: '👤', text: 'Registro y login de usuarios' },
                        { icon: '⚙️', text: 'Panel de administración del vendedor' },
                      ].map(f => (
                        <div className="catalog-feat" key={f.text}>
                          <div className="cf-icon">{f.icon}</div>
                          <span>{f.text}</span>
                        </div>
                      ))}
                    </div>

                    <div className="catalog-actions">
                      <a href="https://unregalo.tumvp.uy" target="_blank" rel="noopener noreferrer" className="btn-live">
                        <span className="live-dot" />
                        Probar demo en vivo
                      </a>
                      <button onClick={openModal} className="btn-outline">
                        Quiero uno así →
                      </button>
                    </div>
                  </div>
                </div>
                <div className="catalog-url">
                  <span className="url-dot" />
                  <a href="https://unregalo.tumvp.uy" target="_blank" rel="noopener noreferrer">unregalo.tumvp.uy</a>
                  <span style={{ marginLeft: '8px', opacity: 0.5 }}>— Registrate como demo y explorá todas las features</span>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <div className="catalog-coming">
                <div className="coming-icon">🧪</div>
                <p><strong>Próximamente más MVPs en vivo.</strong><br />Estamos sumando nuevos prototipos funcionales a este catálogo: SaaS, dashboards, herramientas internas y más. Cada uno disponible para probar online.</p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ========== NOT INCLUDED ========== */}
        <Reveal>
          <section className="not-included">
            <div className="section-label">Transparencia total</div>
            <div className="section-title">Qué no incluye el MVP</div>
            <p className="subtitle">
              Un MVP es un producto mínimo viable — lo justo para validar tu idea. Si después de validar necesitás escalar, podemos cotizarte las siguientes etapas.
            </p>
            <div className="not-list">
              {[
                'App mobile nativa (iOS / Android)',
                'Integraciones complejas con terceros',
                'Soporte o mantenimiento continuo',
                'Features que excedan el scope del MVP',
              ].map(item => (
                <div className="not-item" key={item}>
                  <div className="x-icon"><XIcon /></div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ========== SEO CONTENT ========== */}
        <Reveal>
          <section className="seo-content">
            <div className="content-block">
              <div className="section-label">¿Por qué un MVP?</div>
              <div className="section-title">Desarrollo de MVP en Uruguay:<br />validá antes de invertir</div>

              <p>El mercado uruguayo tiene un ecosistema emprendedor en crecimiento constante. Desde fintech hasta agtech, cada vez más fundadores buscan llevar sus ideas al mercado de forma rápida y eficiente. Sin embargo, el error más común sigue siendo el mismo: invertir demasiado tiempo y dinero en un producto que nadie ha validado.</p>

              <p>Un Producto Mínimo Viable (MVP) resuelve exactamente ese problema. Es la versión más simple y funcional de tu idea — lo justo para ponerla frente a usuarios reales y obtener feedback genuino. No es un mockup ni un prototipo estático: es software real que funciona, con usuarios que lo usan.</p>

              <h3>¿Qué tipo de proyectos son ideales para un MVP?</h3>

              <p>Marketplaces que conectan oferta y demanda, herramientas SaaS verticales para industrias específicas, dashboards que centralizan información dispersa, plataformas de comunidad o membresía, y herramientas internas que resuelven un problema operativo concreto. Si tu idea se puede expresar como "una app web que hace X para Y", probablemente es un buen candidato para MVP.</p>

              <h3>Precio fijo vs. desarrollo por hora</h3>

              <p>En tuMVP.uy trabajamos con precio fijo de USD 950 para tu primer MVP. Esto significa que sabés exactamente cuánto vas a invertir antes de empezar. No hay sorpresas, no hay scope creep, no hay facturas que se disparan. Definimos juntos el alcance, y nos comprometemos a entregarlo en ese presupuesto.</p>

              <h3>Hosting y entrega</h3>

              <p>Ofrecemos dos modalidades: podés elegir que tu MVP se instale y corra en nuestra infraestructura en tumvp.uy, con dominio, SSL y deploy incluido — ideal si querés despreocuparte de lo técnico y enfocarte en validar. O podés descargar el código fuente completo y desplegarlo en tu propia infraestructura con total autonomía. En ambos casos, el código siempre es tuyo.</p>

              <h3>Después del MVP: ¿qué sigue?</h3>

              <p>Si tu MVP valida la hipótesis de negocio y querés seguir construyendo, podemos cotizarte las siguientes fases de desarrollo. Si no valida, perdiste USD 950 y unas semanas en lugar de meses y miles de dólares. De cualquier forma, ganás: información real sobre tu mercado.</p>
            </div>
          </section>
        </Reveal>

        {/* ========== FAQ ========== */}
        <section className="faq" id="faq">
          <Reveal>
            <div className="section-label">Preguntas frecuentes</div>
            <div className="section-title">Lo que todos preguntan</div>
          </Reveal>
          <Reveal>
            <div>
              <FaqItem
                question="¿Qué es exactamente un MVP?"
                answer="Es un Producto Mínimo Viable: la versión más simple de tu idea que funciona y que podés poner frente a usuarios reales. No es un diseño estático ni un PowerPoint — es software real con el que la gente interactúa."
              />
              <FaqItem
                question="¿Cuánto tarda el desarrollo?"
                answer="Depende de la complejidad, pero apuntamos a entre 1 y 3 semanas. La velocidad es parte del servicio — un MVP que tarda meses pierde sentido."
              />
              <FaqItem
                question="¿Qué pasa si necesito cambios después?"
                answer="El código es tuyo. Podés seguir iterando con tu propio equipo, o podemos cotizarte una siguiente fase. No hay lock-in."
              />
              <FaqItem
                question="¿Qué tecnologías usan?"
                answer="Elegimos el stack que mejor se adapte a tu proyecto. Priorizamos tecnologías modernas, con buena comunidad y fáciles de mantener."
              />
              <FaqItem
                question="¿Puedo usar mi propio dominio?"
                answer="Sí. Si elegís nuestro hosting podemos configurar tu dominio personalizado. Si descargás los fuentes, lo configurás en tu propia infra."
              />
              <FaqItem
                question="¿Qué pasa con el hosting después del lanzamiento?"
                answer="El hosting inicial en tumvp.uy está incluido. Si tu proyecto crece y necesitás más recursos, te ayudamos a migrar o escalar."
              />
              <FaqItem
                question="¿El código fuente es mío?"
                answer="Sí, siempre. Recibís el repositorio completo con documentación. Podés modificarlo, auditarlo o entregarlo a otro equipo cuando quieras."
              />
            </div>
          </Reveal>
        </section>

        {/* ========== FINAL CTA ========== */}
        <Reveal>
          <section className="final-cta" id="contacto">
            <div className="section-label">Empezá hoy</div>
            <div className="section-title">¿Tenés la próxima<br /><span style={{ color: 'var(--accent)' }}>idea que falta?</span></div>
            <p>USD 950. Precio fijo. Sin letra chica. Probá nuestros demos, elegí tu modelo y arrancá.</p>
            <button className="cta-main" onClick={openModal}>
              Quiero mi MVP →
            </button>
          </section>
        </Reveal>

      </div>

      {/* ========== FOOTER ========== */}
      <footer className="landing-footer">
        <div className="container">
          <p>© 2026 tuMVP.uy — Desarrollo de MVP y prototipado rápido en Montevideo, Uruguay.</p>
          <p>Powered by <a href="https://mandarinasoftware.uy" target="_blank" rel="noopener noreferrer">Mandarina Software</a></p>
        </div>
      </footer>

      {/* ========== FLOATING CONTROLS ========== */}
      <button className="tour-fab" onClick={startTour} title="Tour guiado">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M5 3l14 8-6.5 2L9 19.5 5 3z" />
        </svg>
      </button>
      <ThemeSelector theme={theme} setTheme={setTheme} />

      {/* ========== GUIDED TOUR ========== */}
      <GuidedTour active={tourActive} onClose={closeTour} />

      {/* ========== MVP MODAL ========== */}
      <MvpModal isOpen={modalOpen} onClose={closeModal} />

    </div>
  );
}

export default LandingPage;
